import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { walletManager, type WalletInfo } from '../utils/walletManager';

interface WalletContextType {
  isConnected: boolean;
  walletInfo: WalletInfo | null;
  isLoading: boolean;
  connectWallet: () => Promise<boolean>;
  disconnectWallet: () => void;
  refreshWalletStatus: () => Promise<void>;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

interface WalletProviderProps {
  children: ReactNode;
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [walletInfo, setWalletInfo] = useState<WalletInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkWalletStatus = async () => {
    try {
      const connected = await walletManager.isConnected();
      const info = walletManager.getWalletInfo();
      setIsConnected(connected);
      setWalletInfo(info);
    } catch (error) {
      console.error('检查钱包状态失败:', error);
      setIsConnected(false);
      setWalletInfo(null);
    } finally {
      setIsLoading(false);
    }
  };

  const connectWallet = async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      const result = await walletManager.connectWallet();
      if (result.success) {
        setIsConnected(true);
        setWalletInfo(result.walletInfo);
        return true;
      }
      return false;
    } catch (error) {
      console.error('连接钱包失败:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const disconnectWallet = () => {
    walletManager.disconnectWallet();
    setIsConnected(false);
    setWalletInfo(null);
  };

  const refreshWalletStatus = async () => {
    await checkWalletStatus();
  };

  useEffect(() => {
    checkWalletStatus();

    // 监听钱包状态变化
    const handleWalletStatusChange = (event: CustomEvent) => {
      const { isConnected: connected, walletInfo: info } = event.detail;
      setIsConnected(connected);
      setWalletInfo(info);
    };

    window.addEventListener('walletStatusChanged', handleWalletStatusChange as EventListener);

    return () => {
      window.removeEventListener('walletStatusChanged', handleWalletStatusChange as EventListener);
    };
  }, []);

  const value: WalletContextType = {
    isConnected,
    walletInfo,
    isLoading,
    connectWallet,
    disconnectWallet,
    refreshWalletStatus,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};
